import { createAxios } from '../lib/axiosFactory';
import _ from 'lodash';
import { errorParser } from '../lib/response/errorParser';
import { ActionResponse } from '../lib/interfaces/actionResponse';
import { ContactUploadStatus } from '../lib/enums/contactUploadStatus';

function getState() {
  return {
    id: null,
    fileName: null,
    blobPath: null,
    fileSize: null,
    permission: null,
    action: 'createAndUpdate',
    preview: [],
    contentApproved: false,
    fileUploadColumnMapping: [],
    listIds: null,
    status: null,
    shouldNumberLookup: null,
    costEstimate: null,
    isFileFormatError: false,
    hasPaymentDetails: false,
    fileUploadError: null
  };
}

export const state = getState();

export const mutations = {
  setId(state: any, id: string): void {
    state.id = id;
  },

  setFileName(state: any, fileName: string): void {
    state.fileName = fileName;
  },

  setBlobPath(state: any, blobPath: string): void {
    state.blobPath = blobPath;
  },

  setFileSize(state: any, fileSize: number): void {
    state.fileSize = fileSize;
  },

  setPermission(state: any, permission: string): void {
    state.permission = permission;
  },

  setAction(state: any, action: string): void {
    state.action = action;
  },

  setPreview(state: any, preview: any[]): void {
    state.preview = preview;
  },

  setContentApproved(state: any, contentApproved: boolean): void {
    state.contentApproved = contentApproved;
  },

  setShouldNumberLookup(state: any, shouldNumberLookup: boolean): void {
    state.shouldNumberLookup = shouldNumberLookup;
  },

  setFileUploadColumnMapping(state: any, fileUploadColumnMapping: any[]): void {
    state.fileUploadColumnMapping = fileUploadColumnMapping;
  },

  setListIds(state: any, listIds: string[] | null): void {
    state.listIds = listIds ? [...listIds] : null;
  },

  setStatus(state: any, status: ContactUploadStatus): void {
    state.status = status;
  },

  setCostEstimate(state: any, costEstimate: string): void {
    state.costEstimate = costEstimate;
  },

  setIsFileFormatError(state: any, isFileFormatError: boolean): void {
    state.isFileFormatError = isFileFormatError;
  },

  setFileUploadError(state: any, error: string): void {
    state.fileUploadError = error;
  },

  setHasPaymentDetails(state: any, hasPaymentDetails: boolean): void {
    state.hasPaymentDetails = hasPaymentDetails;
  },

  setState(state: any, newState: any) {
    Object.keys(state).forEach(key => {
      state[key] = newState[key];
    });
  }
};

export const getters = {
  getPreviewHeaders(state: any): unknown[] {
    return !_.isEmpty(state.preview) ? state.preview[0] : [];
  },

  getPreviewRecords(state: any, getters: any): unknown[] {
    if (_.isEmpty(state.preview)) return [];

    const previewHeaders = getters.getPreviewHeaders;
    const previewRecords = state.preview.slice(1);
    return _.map(previewRecords, x => _.zipObject(previewHeaders, x));
  }
};

export const actions = {
  async createContactUploads(
    { commit, rootState },
    payload: any
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().post(
        '/backend/contact-uploads-create',
        {
          data: {
            type: 'contactUploads',
            attributes: payload
          }
        }
      );

      commit('setId', response.data.data.id);
      commit('setPreview', response.data.data.attributes.preview);
      commit('setStatus', response.data.data.attributes.status);
      commit('setCostEstimate', response.data.data.attributes.costEstimate);
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data,
        status: error.response.status
      };
    }
  },

  async updateContactUploads(
    { commit, state, rootState },
    { payload, id }
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().patch(
        '/backend/contact-uploads-update',
        {
          data: {
            type: 'contactUploads',
            attributes: payload
          }
        },
        {
          params: {
            id: id ?? state.id
          }
        }
      );

      commit('setPreview', response.data.data.attributes.preview);
      commit(
        'setContentApproved',
        response.data.data.attributes.contentApproved
      );
      commit(
        'setShouldNumberLookup',
        response.data.data.attributes.shouldNumberLookup
      );
      commit(
        'setFileUploadColumnMapping',
        response.data.data.attributes.fileUploadColumnMapping
      );
      commit('setListIds', response.data.data.attributes.listIds);
      commit('setStatus', response.data.data.attributes.status);
      commit('setCostEstimate', response.data.data.attributes.costEstimate);
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data,
        status: error.response.status
      };
    }
  },

  async loadContactUpload({
    commit,
    state,
    rootState
  }): Promise<ActionResponse> {
    try {
      const response = await createAxios().get(
        '/backend/contact-uploads-show',
        {
          params: {
            clientId: rootState.user.clientId,
            id: state.id
          }
        }
      );

      commit('setPermission', response.data.data.attributes.permission);
      commit('setAction', response.data.data.attributes.action);
      commit('setBlobPath', response.data.data.attributes.blobPath);
      commit('setFileName', response.data.data.attributes.fileName);
      commit('setPreview', response.data.data.attributes.preview);
      commit(
        'setFileUploadColumnMapping',
        response.data.data.attributes.fileUploadColumnMapping
      );

      if (response.data.data.attributes.contentApproved) {
        commit(
          'setContentApproved',
          response.data.data.attributes.contentApproved
        );
      }
      if (response.data.data.attributes.shouldNumberLookup) {
        commit(
          'setShouldNumberLookup',
          response.data.data.attributes.shouldNumberLookup
        );
      }
      commit('setListIds', response.data.data.attributes.listIds);
      commit('setStatus', response.data.data.attributes.status);
      commit('setCostEstimate', response.data.data.attributes.costEstimate);
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadContactUploads({ commit, rootState }): Promise<ActionResponse> {
    try {
      const response = await createAxios().get(
        '/backend/contact-uploads-index',
        {
          params: {
            clientId: rootState.user.clientId
          }
        }
      );

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async submitContactUploads({ commit, state, rootState }) {
    try {
      const response = await createAxios().post(
        '/backend/contact-upload-submissions-create',
        {
          data: {
            type: 'contactUploadSubmissions'
          }
        },
        {
          params: {
            clientId: rootState.user.clientId,
            contactUploadId: state.id
          }
        }
      );

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  clearUploadFields({ commit, state }, clearId = false): void {
    const newState = getState();
    if (!clearId) newState.id = state.id;
    commit('setState', newState);
  }
};
